import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import styled from 'styled-components';
import { MozartButton } from '@pgforsta/mozart_ui/lib/v4';
import { MozartLoading } from '@pgforsta/mozart_ui';
import Layout from '../Layout/Layout';
import Filters from '../../components/EntityListing/Filters';
import SearchBar from '../../components/EntityListing/SearchBar';
import { Card } from '../../components/styled/Card';
import Table from '../../components/styled/Table';
import ButtonGroup from '../../components/styled/ButtonGroup';
import { ActionButton, ActionIcon } from '../../components/styled/ActionButton';
import { useFilters } from '../../context/filters';
import { getListing } from '../../api/services/entities';
import useAsync from '../../hooks/async';

const CustomCard = styled(Card)`
  overflow: hidden;
`;

const Status = styled.p`
  margin: 15px 0;
  text-align: center;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SearchContainer = styled.div`
  width: 400px;
  max-width: 100%;
  margin-right: 170px;
`;

const LIMIT = 20;

const EntityListing = () => {
  const { filters, updateFilters } = useFilters();
  const history = useHistory();
  const entitiesQuery = useAsync();
  const [entities, setEntities] = useState([]);
  const [resetFlag, setResetFlag] = useState(true);
  const [hasMoreRecords, setHasMoreRecords] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const { searchTerm, entityType, clientId } = filters;

  useEffect(() => {
    setResetFlag(true);
  }, [filters]);

  useEffect(() => {
    if (resetFlag) {
      handleLoadMore(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFlag]);

  const handleSearchSubmit = (term) => {
    updateFilters({ searchTerm: term });
  };

  const handleApplyFilters = (data) => {
    updateFilters(data);
    setShowFilters(false);
  };

  const handleLoadMore = (reset = false) => {
    entitiesQuery.run(
      getListing({
        offset: reset ? 0 : Math.floor(entities.length / LIMIT) * LIMIT,
        limit: LIMIT,
        ...(Boolean(searchTerm) && { searchTerm }),
        ...(Boolean(entityType) && { entityType }),
        ...(clientId !== '0' && { clientId }),
      }).then((data) => {
        const list = data.entityHarvesterDtoList;
        const total = data.noOfEntities;
        const actualList = reset ? list : entities.concat(list);
        setEntities(actualList);
        setHasMoreRecords(
          total === null ? list.length === LIMIT : actualList.length < total
        );
        setResetFlag(false);
        return data;
      })
    );
  };

  const getHeaders = () => {
    return [
      'Entity ID',
      'NPI',
      'Entity Type',
      'Name',
      'Action',
    ];
  };

  const getRows = () => {
    return entities.map((entity) => [
      entity.entityId,
      entity.npi,
      entity.entityType,
      entity.name,
      <ActionButton
        onClick={() => history.push(`/verify-sources/${entity.entityId}`)}
      >
        Verify
        <ActionIcon className="icon-Check" />
      </ActionButton>,
    ]);
  };

  const renderActions = () => {
    return (
      <ActionsContainer>
        <SearchContainer>
          <SearchBar prevTerm={searchTerm} onSubmit={handleSearchSubmit} />
        </SearchContainer>

        <ButtonGroup>
          <MozartButton icon="icon-Filter" onClick={() => setShowFilters(true)}>
            Filter
          </MozartButton>
          <MozartButton outline onClick={() => history.push('/')}>
            Back to Dashboard
          </MozartButton>
        </ButtonGroup>
      </ActionsContainer>
    );
  };

  return (
    <Layout title="Entity Listing" actions={renderActions()}>
      {resetFlag ? (
        <MozartLoading />
      ) : (
        <CustomCard>
          <InfiniteScroll
            initialLoad={false}
            loadMore={() => handleLoadMore()}
            hasMore={entitiesQuery.isResolved && hasMoreRecords}
          >
            <Table
              id="entity-listing-table"
              headers={getHeaders()}
              rows={getRows()}
            />
          </InfiniteScroll>

          {entitiesQuery.isPending && <MozartLoading inline />}

          {entitiesQuery.isRejected && <Status>There was an error</Status>}
        </CustomCard>
      )}

      <Filters
        open={showFilters}
        selectedFilters={{ entityType, clientId }}
        onApply={handleApplyFilters}
        onClose={() => setShowFilters(false)}
      />
    </Layout>
  );
};

export default EntityListing;
